import { format } from 'date-fns'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Download, Groups } from '@mui/icons-material'
import { Alert, Avatar, AvatarGroup, Box, Card, Chip, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { GameIcon } from '../../../features/game'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { borderRadiusDefault } from '../../../themes/theme'
import { Analyst } from '../../../types/Analyst'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalAnalystsQuery } from '../../api/analysts'
import { useGetInternalOrganizationsQuery } from '../../api/organizations'
import { useGetInternalPermissionsQuery } from '../../api/permissions'
import {
  useDeleteInternalReportMutation,
  useDownloadInternalReportFileMutation,
  useGetInternalReportGamesQuery,
  useGetInternalReportsQuery,
} from '../../api/reports'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { OrganizationAccessDialog } from '../../components/OrganizationAccessDialog/OrganizationAccessDialog'
import { countGrantedOrganizations } from '../../components/OrganizationAccessDialog/organizationAccessHelpers'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { filterReports, getReportStatus, reportStatusLabelKeys, reportVisibilityOptions, sortReports } from '../../features/reports/helpers/reportHelpers'
import { InternalReport, InternalReportGame } from '../../features/reports/types/InternalReport'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalPermission, InternalPermissionType } from '../../types/InternalPermission'
import { reportEditPath } from './ReportEditPage'

const MAX_LISTED_GAMES = 3

const statusChipColors = { published: 'success', scheduled: 'info', draft: 'default' } as const

type CustomProps = {
  now: number
  analystsById: Map<string, Analyst>
  gamesById: Map<string, InternalReportGame>
  permissionsById: Map<string, InternalPermission>
  organizationIds: Set<string>
  organizationsLoaded: boolean
  downloadingId?: string
  onDownload: (report: InternalReport) => void
  onEdit: (report: InternalReport) => void
  onAccess: (report: InternalReport) => void
  onDelete: (report: InternalReport) => void
}

const reportTitle = (report: InternalReport) => report.title?.en || '---'

export const ReportsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-reports'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const reportsQuery = useGetInternalReportsQuery()
  const analystsQuery = useGetInternalAnalystsQuery()
  const permissionsQuery = useGetInternalPermissionsQuery(InternalPermissionType.Reports)
  const organizationsQuery = useGetInternalOrganizationsQuery()
  const gameIds = useMemo(() => Array.from(new Set((reportsQuery.data ?? []).flatMap((report) => report.gameIds ?? []))).sort(), [reportsQuery.data])
  const gamesQuery = useGetInternalReportGamesQuery(gameIds, { skip: gameIds.length === 0 })
  const [deleteReport] = useDeleteInternalReportMutation()
  const [downloadFile] = useDownloadInternalReportFileMutation()
  const [downloadingId, setDownloadingId] = useState<string>()
  const [search, setSearch] = useState('')
  const [accessReport, setAccessReport] = useState<InternalReport>()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<InternalReport>>()

  const rows = useMemo(() => filterReports(sortReports(reportsQuery.data ?? []), search), [reportsQuery.data, search])

  const tableColumns: GRTableColumn<InternalReport, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:reports_analysts'),
        accessor: ({ row, customTableProps }) => (
          <AvatarGroup max={4} sx={{ justifyContent: 'flex-end', '& .MuiAvatar-root': { width: 32, height: 32 } }}>
            {(row.analysts ?? []).map((id) => {
              const analyst = customTableProps?.analystsById.get(id)
              return (
                <Tooltip key={id} title={analyst?.name ?? id}>
                  <Avatar alt={analyst?.name ?? id} src={analyst?.picture} />
                </Tooltip>
              )
            })}
          </AvatarGroup>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 144, minWidth: 144 } },
      },
      {
        labelAccessor: t('internal-analysts:reports_title'),
        accessor: ({ row }) => <Typography fontWeight="bold">{reportTitle(row)}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => reportTitle(row).toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-analysts:reports_related_games'),
        accessor: ({ row, customTableProps }) => {
          const games = (row.gameIds ?? []).map((id) => customTableProps?.gamesById.get(id)).filter((game): game is InternalReportGame => !!game)
          if (!games.length) return null
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {games.slice(0, MAX_LISTED_GAMES).map((game) => (
                <Tooltip key={game.id} title={game.name}>
                  <Box>
                    <GameIcon src={game.icons?.[0] ?? game.icon} gameName={game.name} size="small" />
                  </Box>
                </Tooltip>
              ))}
              {games.length > MAX_LISTED_GAMES && (
                <Typography variant="body2" color="text.secondary">
                  +{games.length - MAX_LISTED_GAMES}
                </Typography>
              )}
            </Stack>
          )
        },
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-analysts:reports_visibility'),
        accessor: ({ row }) => {
          const option = reportVisibilityOptions.find(({ value }) => value === row.visibility)
          return option ? t(option.labelKey) : row.visibility
        },
        sortable: true,
        sortAccessor: ({ row }) => row.visibility,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-analysts:reports_published_at'),
        accessor: ({ row }) => (row.published ? format(row.publishedAt, 'dd.MM.yyyy HH:mm') : ''),
        sortable: true,
        sortAccessor: ({ row }) => (row.published ? row.publishedAt : 0),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-analysts:reports_status'),
        accessor: ({ row, customTableProps }) => {
          const status = getReportStatus(row, customTableProps?.now)
          return <Chip size="small" color={statusChipColors[status]} label={t(reportStatusLabelKeys[status])} />
        },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => getReportStatus(row, customTableProps?.now),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 100 } },
      },
      {
        labelAccessor: t('internal-analysts:reports_access'),
        accessor: ({ row, customTableProps }) =>
          customTableProps?.organizationsLoaded
            ? countGrantedOrganizations(customTableProps.permissionsById.get(row.id), customTableProps.organizationIds)
            : '–',
        sortable: true,
        sortAccessor: ({ row, customTableProps }) =>
          customTableProps ? countGrantedOrganizations(customTableProps.permissionsById.get(row.id), customTableProps.organizationIds) : 0,
      },
      rowActionsColumn<InternalReport, CustomProps>({
        width: 160,
        actions: ({ row, customTableProps }) => [
          {
            icon: <Download />,
            label: t('internal-analysts:reports_download'),
            ariaLabel: t('internal-analysts:reports_download_named', { name: reportTitle(row) }),
            size: 'small',
            disabled: customTableProps?.downloadingId === row.id,
            onClick: () => customTableProps?.onDownload(row),
          },
          {
            type: 'edit',
            label: t('internal-analysts:reports_edit'),
            ariaLabel: t('internal-analysts:reports_edit_named', { name: reportTitle(row) }),
            size: 'small',
            onClick: () => customTableProps?.onEdit(row),
          },
          {
            icon: <Groups />,
            label: t('internal-analysts:reports_access_edit'),
            ariaLabel: t('internal-analysts:reports_access_edit_named', { name: reportTitle(row) }),
            size: 'small',
            onClick: () => customTableProps?.onAccess(row),
          },
          {
            type: 'delete',
            label: t('internal-analysts:reports_delete'),
            ariaLabel: t('internal-analysts:reports_delete_named', { name: reportTitle(row) }),
            size: 'small',
            onClick: () => customTableProps?.onDelete(row),
          },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const onDownload = useCallback(
    async (report: InternalReport) => {
      setDownloadingId(report.id)
      await run(downloadFile(report.id).unwrap(), { error: t('internal-analysts:reports_download_failed') })
      setDownloadingId(undefined)
    },
    [downloadFile, run, t]
  )

  // The time of the last load, so the statuses match the list they were computed for.
  const now = reportsQuery.fulfilledTimeStamp ?? Date.now()
  const customProps = useMemo<CustomProps>(
    () => ({
      now,
      analystsById: new Map((analystsQuery.data ?? []).map((analyst) => [analyst.id, analyst])),
      gamesById: new Map((gamesQuery.data ?? []).map((game) => [game.id, game])),
      permissionsById: new Map((permissionsQuery.data ?? []).map((permission) => [permission.id, permission])),
      organizationIds: new Set((organizationsQuery.data ?? []).map(({ id }) => id)),
      organizationsLoaded: organizationsQuery.isSuccess && permissionsQuery.isSuccess,
      downloadingId,
      onDownload,
      onEdit: (report) => navigate(reportEditPath(report.id)),
      onAccess: setAccessReport,
      onDelete: (report) =>
        setConfirmDialogData({
          title: t('internal-analysts:reports_delete'),
          confirmText: t(report.published ? 'internal-analysts:reports_delete_published_confirm' : 'internal-analysts:reports_delete_confirm', {
            name: reportTitle(report),
          }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: report,
        }),
    }),
    [
      analystsQuery.data,
      downloadingId,
      gamesQuery.data,
      navigate,
      now,
      onDownload,
      organizationsQuery.data,
      organizationsQuery.isSuccess,
      permissionsQuery.data,
      permissionsQuery.isSuccess,
      t,
    ]
  )

  const { refetch } = reportsQuery
  const refetchPermissions = permissionsQuery.refetch
  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<InternalReport>) => {
      setConfirmDialogData(undefined)
      const report = confirmed?.data
      if (!report) return
      const name = reportTitle(report)
      let failed = false
      try {
        await deleteReport(report.id).unwrap()
      } catch {
        failed = true
      }
      try {
        const remaining = await refetch().unwrap()
        refetchPermissions()
        if (remaining.some(({ id }) => id === report.id)) {
          notifyError(t(failed ? 'internal-analysts:reports_delete_failed' : 'internal-analysts:reports_not_deleted', { name }))
          return
        }
        notifySuccess(t('internal-analysts:reports_deleted', { name }))
      } catch {
        notifyError(t('internal-analysts:reports_delete_unknown', { name }))
      }
    },
    [deleteReport, notifyError, notifySuccess, refetch, refetchPermissions, t]
  )

  return (
    <Box>
      <InternalListToolbar
        search={{ value: search, onChange: setSearch, placeholder: t('internal-analysts:reports_search') }}
        add={{ label: t('internal-analysts:reports_add'), onClick: () => navigate(reportEditPath()) }}
      >
        {reportsQuery.data && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:reports_count', { count: rows.length, total: reportsQuery.data.length })}
          </Typography>
        )}
      </InternalListToolbar>

      {(permissionsQuery.isError || organizationsQuery.isError) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('internal-analysts:reports_access_load_failed')}
        </Alert>
      )}

      <QueryStateBoundary isLoading={reportsQuery.isLoading} isError={reportsQuery.isError} errorText={t('internal-analysts:reports_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-analysts:reports_none')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      {accessReport && (
        <OrganizationAccessDialog
          open
          type={InternalPermissionType.Reports}
          itemId={accessReport.id}
          itemName={reportTitle(accessReport)}
          target={
            accessReport.coverUrls?.[0] ? (
              <Box
                component="img"
                src={accessReport.coverUrls[0]}
                alt={reportTitle(accessReport)}
                sx={{ height: 120, objectFit: 'contain', alignSelf: 'flex-start', borderRadius: borderRadiusDefault }}
              />
            ) : undefined
          }
          onClose={() => setAccessReport(undefined)}
        />
      )}

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
